/**
 * Generic column actions menu.
 * Reorder is via drag handle only — no Move left/right.
 * Sort / Filter use nested DropdownMenu.Sub flyouts.
 */

import React from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '../../atoms/Button';
import { DropdownMenu } from '../DropdownMenu';

export interface KanbanColumnMenuSortOption {
  id: string;
  label: string;
}

export interface KanbanColumnMenuFilterDef {
  id: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

export interface KanbanColumnMenuProps {
  canRename?: boolean;
  canDelete?: boolean;
  onRename?: () => void;
  onDelete?: () => void;
  /** Per-column sort (independent of board-wide sort). */
  sortOptions?: KanbanColumnMenuSortOption[];
  sortBy?: string | null;
  onSortByChange?: (sortBy: string | null) => void;
  /** Per-column filters (independent of board-wide filters). */
  filterDefs?: KanbanColumnMenuFilterDef[];
  activeFilters?: Record<string, string | null>;
  onActiveFiltersChange?: (filters: Record<string, string | null>) => void;
  labels?: {
    menu?: string;
    rename?: string;
    delete?: string;
    sort?: string;
    filter?: string;
    sortManual?: string;
    filterAll?: string;
  };
}

export function KanbanColumnMenu({
  canRename = false,
  canDelete = false,
  onRename,
  onDelete,
  sortOptions = [],
  sortBy = null,
  onSortByChange,
  filterDefs = [],
  activeFilters = {},
  onActiveFiltersChange,
  labels = {},
}: KanbanColumnMenuProps) {
  const canSort = sortOptions.length > 0 && Boolean(onSortByChange);
  const canFilter = filterDefs.length > 0 && Boolean(onActiveFiltersChange);
  const hasItems = canRename || canDelete || canSort || canFilter;
  if (!hasItems) return null;

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<MoreVertical />}
          aria-label={labels.menu ?? 'Column actions'}
          onPointerDown={(event) => event.stopPropagation()}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className="min-w-[168px]">
        {canRename ? (
          <DropdownMenu.Item onClick={onRename}>
            {labels.rename ?? 'Rename'}
          </DropdownMenu.Item>
        ) : null}

        {canSort ? (
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger>
              {labels.sort ?? 'Sort'}
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent className="min-w-[148px]">
              <DropdownMenu.Item
                onClick={() => onSortByChange?.(null)}
                className={sortBy == null ? 'font-semibold' : undefined}
              >
                {labels.sortManual ?? 'Manual'}
              </DropdownMenu.Item>
              {sortOptions.map((option) => (
                <DropdownMenu.Item
                  key={option.id}
                  onClick={() => onSortByChange?.(option.id)}
                  className={
                    sortBy === option.id ? 'font-semibold' : undefined
                  }
                >
                  {option.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        ) : null}

        {canFilter ? (
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger>
              {labels.filter ?? 'Filter'}
            </DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent className="min-w-[160px]">
              {filterDefs.map((def, index) => (
                <React.Fragment key={def.id}>
                  {index > 0 ? <DropdownMenu.Separator /> : null}
                  <DropdownMenu.Label>{def.label}</DropdownMenu.Label>
                  <DropdownMenu.Item
                    onClick={() =>
                      onActiveFiltersChange?.({
                        ...activeFilters,
                        [def.id]: null,
                      })
                    }
                    className={
                      activeFilters[def.id] == null
                        ? 'font-semibold'
                        : undefined
                    }
                  >
                    {labels.filterAll ?? 'All'}
                  </DropdownMenu.Item>
                  {def.options.map((option) => (
                    <DropdownMenu.Item
                      key={option.value}
                      onClick={() =>
                        onActiveFiltersChange?.({
                          ...activeFilters,
                          [def.id]: option.value,
                        })
                      }
                      className={
                        activeFilters[def.id] === option.value
                          ? 'font-semibold'
                          : undefined
                      }
                    >
                      {option.label}
                    </DropdownMenu.Item>
                  ))}
                </React.Fragment>
              ))}
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        ) : null}

        {canDelete ? (
          <>
            {(canRename || canSort || canFilter) && <DropdownMenu.Separator />}
            <DropdownMenu.Item variant="destructive" onClick={onDelete}>
              {labels.delete ?? 'Delete'}
            </DropdownMenu.Item>
          </>
        ) : null}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
